/**
 * Email mailbox lifecycle events — catalog 1.12.0 additions
 * (commerce-platform continuous tracks, T06 Part A: registered-mailbox CRUD).
 *
 * Emitted by the email service's mailboxes module from INSIDE the same
 * serializable CRDB transaction as the row write (transactional outbox —
 * packages/sql/src/outbox.ts `writeOutbox`), relayed by the existing email
 * outbox relay to the durable bus.
 *
 * - `email:mailbox-registered` — a mailbox was registered for a tenant
 *   (mode send|receive|both). Auditable: every registration is
 *   reconstructable (plan §9 audit coverage).
 * - `email:mailbox-updated`    — a mailbox's configuration changed; payload
 *   lists the changed fields. Auditable.
 * - `email:mailbox-disabled`   — a mailbox was SOFT-disabled (status →
 *   'disabled' via DELETE; the row is never hard-deleted). Auditable.
 *
 * SECRETS BOUNDARY (hard gate): payloads carry the mailbox id/address/mode/
 * status and actor — NEVER the vault reference value and never any credential
 * material.
 *
 * `email:received` (catalog 1.15.0, T06 Part B) is the inbound-parse event:
 * emitted by the email receive worker from INSIDE the same serializable CRDB
 * transaction that inserts the `ReceivedMessage` row (transactional outbox),
 * relayed by the existing email outbox relay. Consumers (docs/receive/
 * consumer-wiring.md): communication webhook matcher, support-ticket
 * auto-creation (gated per mailbox by `MailboxConfig.autoCreateTickets`),
 * agent trigger bindings. Payload carries message PROJECTION fields only —
 * it carries the same PII any email notification carries (sender/subject/
 * body excerpts) and NEVER credential material or attachment bytes.
 *
 * NOTE (drift R-5): the tracks plan text says "catalog v14" — plan fiction.
 * Part A landed in the real release 1.12.0; voice followed at 1.13.0 and
 * decision at 1.14.0. These Part B definitions land in the next real
 * release, `since: '1.15.0'`.
 */
import {
  asEventRecord,
  defineEvent,
  optionalString,
  requireString,
  requireStringArray,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface MailboxRegisteredPayload extends Record<string, unknown> {
  mailboxId: string;
  orgId: string;
  address: string;
  mode: string;
  status: string;
  /** Present only for receive-capable mailboxes (mode receive|both). */
  protocol?: string;
  registeredAt: string;
  changedBy: string;
}

export interface MailboxUpdatedPayload extends Record<string, unknown> {
  mailboxId: string;
  orgId: string;
  address: string;
  mode: string;
  status: string;
  protocol?: string;
  updatedAt: string;
  changedFields: string[];
  changedBy: string;
}

export interface MailboxDisabledPayload extends Record<string, unknown> {
  mailboxId: string;
  orgId: string;
  address: string;
  mode: string;
  disabledAt: string;
  disabledBy: string;
}

const MAILBOX_REGISTERED_TYPE = 'email:mailbox-registered';
const mailboxRegisteredSchema: EventSchema<MailboxRegisteredPayload> = {
  parse(data) {
    const record = asEventRecord(data, MAILBOX_REGISTERED_TYPE);
    const protocol = optionalString(record.protocol, 'protocol', MAILBOX_REGISTERED_TYPE);
    return {
      mailboxId: requireString(record.mailboxId, 'mailboxId', MAILBOX_REGISTERED_TYPE),
      orgId: requireString(record.orgId, 'orgId', MAILBOX_REGISTERED_TYPE),
      address: requireString(record.address, 'address', MAILBOX_REGISTERED_TYPE),
      mode: requireString(record.mode, 'mode', MAILBOX_REGISTERED_TYPE),
      status: requireString(record.status, 'status', MAILBOX_REGISTERED_TYPE),
      ...(protocol === undefined ? {} : { protocol }),
      registeredAt: requireString(record.registeredAt, 'registeredAt', MAILBOX_REGISTERED_TYPE),
      changedBy: requireString(record.changedBy, 'changedBy', MAILBOX_REGISTERED_TYPE),
    };
  },
};

export const EMAIL_MAILBOX_REGISTERED_EVENT = defineEvent<MailboxRegisteredPayload>({
  type: MAILBOX_REGISTERED_TYPE,
  version: 1,
  schema: mailboxRegisteredSchema,
  auditable: true,
  description: 'A mailbox was registered for a tenant (configuration fields only — the secrets-vault reference VALUE and any credential material never appear in event payloads).',
  since: '1.12.0',
});

const MAILBOX_UPDATED_TYPE = 'email:mailbox-updated';
const mailboxUpdatedSchema: EventSchema<MailboxUpdatedPayload> = {
  parse(data) {
    const record = asEventRecord(data, MAILBOX_UPDATED_TYPE);
    const protocol = optionalString(record.protocol, 'protocol', MAILBOX_UPDATED_TYPE);
    return {
      mailboxId: requireString(record.mailboxId, 'mailboxId', MAILBOX_UPDATED_TYPE),
      orgId: requireString(record.orgId, 'orgId', MAILBOX_UPDATED_TYPE),
      address: requireString(record.address, 'address', MAILBOX_UPDATED_TYPE),
      mode: requireString(record.mode, 'mode', MAILBOX_UPDATED_TYPE),
      status: requireString(record.status, 'status', MAILBOX_UPDATED_TYPE),
      ...(protocol === undefined ? {} : { protocol }),
      updatedAt: requireString(record.updatedAt, 'updatedAt', MAILBOX_UPDATED_TYPE),
      changedFields: requireStringArray(record.changedFields, 'changedFields', MAILBOX_UPDATED_TYPE),
      changedBy: requireString(record.changedBy, 'changedBy', MAILBOX_UPDATED_TYPE),
    };
  },
};

export const EMAIL_MAILBOX_UPDATED_EVENT = defineEvent<MailboxUpdatedPayload>({
  type: MAILBOX_UPDATED_TYPE,
  version: 1,
  schema: mailboxUpdatedSchema,
  auditable: true,
  description: 'A registered mailbox changed (changedFields names the diff; no vault values or credential material ever appear).',
  since: '1.12.0',
});

const MAILBOX_DISABLED_TYPE = 'email:mailbox-disabled';
const mailboxDisabledSchema: EventSchema<MailboxDisabledPayload> = {
  parse(data) {
    const record = asEventRecord(data, MAILBOX_DISABLED_TYPE);
    return {
      mailboxId: requireString(record.mailboxId, 'mailboxId', MAILBOX_DISABLED_TYPE),
      orgId: requireString(record.orgId, 'orgId', MAILBOX_DISABLED_TYPE),
      address: requireString(record.address, 'address', MAILBOX_DISABLED_TYPE),
      mode: requireString(record.mode, 'mode', MAILBOX_DISABLED_TYPE),
      disabledAt: requireString(record.disabledAt, 'disabledAt', MAILBOX_DISABLED_TYPE),
      disabledBy: requireString(record.disabledBy, 'disabledBy', MAILBOX_DISABLED_TYPE),
    };
  },
};

export const EMAIL_MAILBOX_DISABLED_EVENT = defineEvent<MailboxDisabledPayload>({
  type: MAILBOX_DISABLED_TYPE,
  version: 1,
  schema: mailboxDisabledSchema,
  auditable: true,
  description: 'A registered mailbox was soft-disabled (status=disabled; the row is retained for audit and address-uniqueness).',
  since: '1.12.0',
});

export const EMAIL_MAILBOX_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [EMAIL_MAILBOX_REGISTERED_EVENT.type]: EMAIL_MAILBOX_REGISTERED_EVENT,
  [EMAIL_MAILBOX_UPDATED_EVENT.type]: EMAIL_MAILBOX_UPDATED_EVENT,
  [EMAIL_MAILBOX_DISABLED_EVENT.type]: EMAIL_MAILBOX_DISABLED_EVENT,
};

// ---------------------------------------------------------------------------
// email:received — inbound-parse event (T06 Part B, catalog 1.15.0)
// ---------------------------------------------------------------------------

/** Attachment metadata carried by the event (bytes never transit events). */
export interface EmailReceivedAttachment {
  filename: string;
  contentType: string;
  size: number;
  sha256: string;
}

export interface EmailReceivedPayload extends Record<string, unknown> {
  /** ReceivedMessage row id (the aggregate). */
  receivedMessageId: string;
  mailboxId: string;
  orgId: string;
  /** Mailbox address that received the message (destination identity). */
  mailboxAddress: string;
  fromAddress: string;
  fromName?: string;
  to: string[];
  subject?: string;
  /** RFC 3339 UTC. */
  receivedAt: string;
  /** Sender-declared Date header (RFC 3339 UTC) when parseable. */
  date?: string;
  /** Up to EMAIL_RECEIVED_BODY_EXCERPT_MAX chars of the text body (consumers
   *  needing the full body read the ReceivedMessage row by id). */
  bodyExcerpt?: string;
  attachments: EmailReceivedAttachment[];
  /** Per-mailbox idempotency claim key (uidValidity:uid | Message-ID | digest). */
  messageKey: string;
  /** The mailbox's autoCreateTickets flag at receive time — the support-ticket
   *  consumer acts ONLY when this is true (docs/receive/consumer-wiring.md). */
  autoCreateTickets: boolean;
  /** Identity of the receive worker run (observability; never secret material). */
  processedBy?: string;
}

/** Body excerpt ceiling carried in the event payload (chars). */
export const EMAIL_RECEIVED_BODY_EXCERPT_MAX = 500;

const EMAIL_RECEIVED_TYPE = 'email:received';

function parseAttachment(value: unknown, index: number): EmailReceivedAttachment {
  const record = asEventRecord(value, `${EMAIL_RECEIVED_TYPE}.attachments[${index}]`);
  const filename = record.filename;
  const contentType = record.contentType;
  const size = record.size;
  const sha256 = record.sha256;
  if (typeof filename !== 'string') {
    throw new Error(`${EMAIL_RECEIVED_TYPE}: attachments[${index}].filename must be a string`);
  }
  if (typeof contentType !== 'string') {
    throw new Error(`${EMAIL_RECEIVED_TYPE}: attachments[${index}].contentType must be a string`);
  }
  if (typeof size !== 'number' || !Number.isInteger(size) || size < 0) {
    throw new Error(`${EMAIL_RECEIVED_TYPE}: attachments[${index}].size must be a non-negative integer`);
  }
  if (typeof sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(sha256)) {
    throw new Error(`${EMAIL_RECEIVED_TYPE}: attachments[${index}].sha256 must be 64 hex chars`);
  }
  return { filename, contentType, size, sha256 };
}

function requireBoolean(value: unknown, field: string, context: string): boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`${context}: ${field} must be a boolean`);
  }
  return value;
}

const emailReceivedSchema: EventSchema<EmailReceivedPayload> = {
  parse(data) {
    const record = asEventRecord(data, EMAIL_RECEIVED_TYPE);
    const to = requireStringArray(record.to, 'to', EMAIL_RECEIVED_TYPE);
    const rawAttachments = Array.isArray(record.attachments) ? record.attachments : [];
    return {
      receivedMessageId: requireString(record.receivedMessageId, 'receivedMessageId', EMAIL_RECEIVED_TYPE),
      mailboxId: requireString(record.mailboxId, 'mailboxId', EMAIL_RECEIVED_TYPE),
      orgId: requireString(record.orgId, 'orgId', EMAIL_RECEIVED_TYPE),
      mailboxAddress: requireString(record.mailboxAddress, 'mailboxAddress', EMAIL_RECEIVED_TYPE),
      fromAddress: requireString(record.fromAddress, 'fromAddress', EMAIL_RECEIVED_TYPE),
      fromName: optionalString(record.fromName, 'fromName', EMAIL_RECEIVED_TYPE),
      to,
      subject: optionalString(record.subject, 'subject', EMAIL_RECEIVED_TYPE),
      receivedAt: requireString(record.receivedAt, 'receivedAt', EMAIL_RECEIVED_TYPE),
      date: optionalString(record.date, 'date', EMAIL_RECEIVED_TYPE),
      bodyExcerpt: optionalString(record.bodyExcerpt, 'bodyExcerpt', EMAIL_RECEIVED_TYPE),
      attachments: rawAttachments.map((att, i) => parseAttachment(att, i)),
      messageKey: requireString(record.messageKey, 'messageKey', EMAIL_RECEIVED_TYPE),
      autoCreateTickets: requireBoolean(record.autoCreateTickets, 'autoCreateTickets', EMAIL_RECEIVED_TYPE),
      processedBy: optionalString(record.processedBy, 'processedBy', EMAIL_RECEIVED_TYPE),
    } as EmailReceivedPayload;
  },
};

export const EMAIL_RECEIVED_EVENT = defineEvent<EmailReceivedPayload>({
  type: EMAIL_RECEIVED_TYPE,
  version: 1,
  schema: emailReceivedSchema,
  auditable: true,
  description:
    'An inbound email was received and parsed by a mailbox poll worker (message projection: sender, recipients, subject, body excerpt, attachment METADATA only — never attachment bytes, vault references, or credential material).',
  since: '1.15.0',
});

export const EMAIL_RECEIVED_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [EMAIL_RECEIVED_EVENT.type]: EMAIL_RECEIVED_EVENT,
};

